<script setup lang="ts">
/**
 * Application shell of the HORIZON advisor workstation.
 *
 * Layout: the 232px sidebar rail, the 64px top bar and the routed screen. From
 * 1280px upwards the rail is permanent; below 900px it becomes an off canvas
 * drawer opened by the burger button, closed by Escape or by a click on the
 * backdrop, with the focus trapped inside while it is open.
 *
 * The shell disappears entirely on /connexion, which owns the full viewport.
 * A single network banner is shown here for the whole application: any store
 * reporting a transport failure surfaces it, and the retry replays that very
 * store. Server failures stay inside the screen that issued the request, with
 * their request identifier.
 */
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { HzSidebar, HzTopbar } from '@horizon/ui';
import type { HzSuggestion } from '@horizon/ui';
import { rechercherClients } from '@horizon/api-client';
import type { ErreurHorizon } from '@horizon/api-client';
import { LIBELLE_STATUT_KYC } from '@/composables/useFormatsFr';
import BandeauReseau from '@/composants/BandeauReseau.vue';
import MenuCompte from '@/composants/MenuCompte.vue';
import PanneauNotifications from '@/composants/PanneauNotifications.vue';
import TiroirNavigation from '@/composants/TiroirNavigation.vue';
import ZoneToasts from '@/composants/ZoneToasts.vue';
import { LIBELLE_ROLE } from '@/composables/useFormatsFr';
import { useRaccourcis } from '@/composables/useRaccourcis';
import { useToasts } from '@/composables/useToasts';
import { useClientCourantStore } from '@/stores/clientCourant';
import { useEcheancierStore } from '@/stores/echeancier';
import { useNotificationsStore } from '@/stores/notifications';
import { useSessionStore } from '@/stores/session';
import { useSimulationStore } from '@/stores/simulation';
import { ENTREES_NAVIGATION, cleDepuisRoute, entreeParCle } from '@/utils/navigation';

/** Debounce of the global search before the client list is filtered (EF-011). */
const DELAI_RECHERCHE_MS = 300;

const route = useRoute();
const router = useRouter();
const session = useSessionStore();
const clientCourant = useClientCourantStore();
const simulation = useSimulationStore();
const echeancier = useEcheancierStore();
const notifications = useNotificationsStore();
const { information } = useToasts();

const tiroirOuvert = ref(false);
const menuCompteOuvert = ref(false);
const panneauNotificationsOuvert = ref(false);
const recherche = ref('');
const repriseEnCours = ref(false);

let minuterieRecherche: ReturnType<typeof setTimeout> | null = null;

const coquilleVisible = computed(() => route.meta.public !== true && session.estAuthentifie);

const cleActive = computed(() => cleDepuisRoute(typeof route.name === 'string' ? route.name : ''));

const filAriane = computed(() => {
  const parent = entreeParCle(cleActive.value)?.label ?? 'HORIZON';
  const titre = typeof route.meta.titre === 'string' ? route.meta.titre : '';
  return titre === '' || titre === parent ? { parent } : { parent, current: titre };
});

const libelleRole = computed(() =>
  session.role === null ? 'Rôle non attribué' : LIBELLE_ROLE[session.role],
);

/** Minimal contract every store honours for the shared network banner. */
interface MagasinSurveille {
  readonly erreurReseau: ErreurHorizon | null;
  readonly reessayer: () => Promise<void>;
}

/** Stores able to report a transport failure, in the order the banner inspects them. */
const magasinsSurveilles: readonly MagasinSurveille[] = [
  clientCourant,
  simulation,
  echeancier,
  notifications,
];

const magasinEnIncident = computed(
  () => magasinsSurveilles.find((magasin) => magasin.erreurReseau !== null) ?? null,
);
const incidentReseau = computed(() => magasinEnIncident.value?.erreurReseau ?? null);

async function reessayerReseau(): Promise<void> {
  const magasin = magasinEnIncident.value;
  if (magasin === null || repriseEnCours.value) {
    return;
  }
  repriseEnCours.value = true;
  try {
    await magasin.reessayer();
  } finally {
    repriseEnCours.value = false;
  }
}

function fermerSurcouches(): void {
  tiroirOuvert.value = false;
  menuCompteOuvert.value = false;
  panneauNotificationsOuvert.value = false;
}

function naviguer(cle: string): void {
  const entree = entreeParCle(cle);
  if (entree === undefined) {
    return;
  }
  fermerSurcouches();
  void router.push({ name: entree.route });
}

function ouvrirAide(): void {
  information("Assistance HORIZON : contactez l'administrateur de votre cabinet.");
}

function basculerNotifications(): void {
  menuCompteOuvert.value = false;
  panneauNotificationsOuvert.value = !panneauNotificationsOuvert.value;
}

function basculerMenuCompte(): void {
  panneauNotificationsOuvert.value = false;
  menuCompteOuvert.value = !menuCompteOuvert.value;
}

async function deconnecter(): Promise<void> {
  fermerSurcouches();
  notifications.reinitialiser();
  clientCourant.reinitialiser();
  simulation.reinitialiser();
  echeancier.reinitialiser();
  await session.deconnecter();
}

useRaccourcis({ surEchappement: fermerSurcouches, actif: () => coquilleVisible.value });

// Global search: suggestions under the field, exactly as the design reference. Up to
// five clients from the caller perimeter and three products, eight entries in all.
// Choosing a client opens their simulator; no navigation happens while typing.
const PRODUITS = [
  'Assurance vie Horizon Sérénité',
  'PER Horizon Avenir',
  'Compte-titres Horizon Croissance',
] as const;
const suggestions = ref<readonly HzSuggestion[]>([]);

watch(recherche, (terme) => {
  if (minuterieRecherche !== null) {
    clearTimeout(minuterieRecherche);
  }
  const requete = terme.trim();
  if (requete.length < 2) {
    suggestions.value = [];
    return;
  }
  minuterieRecherche = setTimeout(() => {
    void (async () => {
      const normalise = requete.toLocaleLowerCase('fr-FR');
      let clients: HzSuggestion[] = [];
      try {
        clients = (await rechercherClients(requete)).slice(0, 5).map((c) => ({
          id: c.id,
          initiales: `${c.prenom[0] ?? ''}${c.nom[0] ?? ''}`.toUpperCase(),
          libelle: `${c.prenom} ${c.nom}`,
          detail: `Client · ${c.profil_investisseur} · ${LIBELLE_STATUT_KYC[c.statut_kyc]}`,
          genre: 'client' as const,
        }));
      } catch {
        clients = [];
      }
      const produits: HzSuggestion[] = PRODUITS.filter((nom) =>
        nom.toLocaleLowerCase('fr-FR').includes(normalise),
      )
        .slice(0, 3)
        .map((nom) => ({
          id: nom,
          initiales: 'P',
          libelle: nom,
          detail: 'Produit · référentiel v1',
          genre: 'produit' as const,
        }));
      suggestions.value = [...clients, ...produits].slice(0, 8);
    })();
  }, DELAI_RECHERCHE_MS);
});

function choisirSuggestion(s: HzSuggestion): void {
  suggestions.value = [];
  if (s.genre === 'client') {
    void router.push({ name: 'simulateur', params: { clientId: s.id } });
    return;
  }
  information(`${s.libelle} : fiche produit disponible dans le référentiel.`);
}

// Any navigation dismisses the transient surfaces of the shell.
watch(() => route.fullPath, fermerSurcouches);

// The realtime channel follows the signed in advisor, and stops with the session.
watch(
  () => session.profil?.user_id ?? null,
  (utilisateurId) => {
    if (utilisateurId === null) {
      notifications.reinitialiser();
      return;
    }
    void notifications.charger();
    notifications.sAbonner(utilisateurId);
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  if (minuterieRecherche !== null) {
    clearTimeout(minuterieRecherche);
  }
  notifications.arreterAbonnement();
});
</script>

<template>
  <div
    v-if="!coquilleVisible"
    class="hz-app__plein"
  >
    <RouterView />
    <ZoneToasts />
  </div>

  <div
    v-else
    class="hz-app"
  >
    <a
      class="hz-lien-evitement"
      href="#hz-contenu"
    >Aller au contenu principal</a>

    <div class="hz-app__rail">
      <HzSidebar
        :items="ENTREES_NAVIGATION"
        :active-key="cleActive"
        @navigate="naviguer"
        @contact="ouvrirAide"
      />
    </div>

    <TiroirNavigation
      :ouvert="tiroirOuvert"
      @fermer="tiroirOuvert = false"
    >
      <HzSidebar
        :items="ENTREES_NAVIGATION"
        :active-key="cleActive"
        @navigate="naviguer"
        @contact="ouvrirAide"
      />
    </TiroirNavigation>

    <div class="hz-app__colonne">
      <div class="hz-app__entete">
        <button
          type="button"
          class="hz-app__burger"
          aria-label="Ouvrir la navigation"
          aria-haspopup="dialog"
          :aria-expanded="tiroirOuvert"
          @click="tiroirOuvert = true"
        >
          <svg
            viewBox="0 0 18 14"
            aria-hidden="true"
            focusable="false"
          >
            <line
              x1="1"
              y1="1.6"
              x2="17"
              y2="1.6"
              stroke="currentColor"
              stroke-width="1.7"
              stroke-linecap="round"
            />
            <line
              x1="1"
              y1="7"
              x2="17"
              y2="7"
              stroke="currentColor"
              stroke-width="1.7"
              stroke-linecap="round"
            />
            <line
              x1="1"
              y1="12.4"
              x2="17"
              y2="12.4"
              stroke="currentColor"
              stroke-width="1.7"
              stroke-linecap="round"
            />
          </svg>
        </button>

        <HzTopbar
          v-model:search="recherche"
          class="hz-app__topbar"
          :breadcrumb="filAriane"
          :notifications="notifications.nonLues"
          :user-initials="session.initiales"
          :suggestions="suggestions"
          @notifications="basculerNotifications"
          @account="basculerMenuCompte"
          @choisir="choisirSuggestion"
        />

        <PanneauNotifications
          :ouvert="panneauNotificationsOuvert"
          @fermer="panneauNotificationsOuvert = false"
        />

        <MenuCompte
          :ouvert="menuCompteOuvert"
          :nom="session.nomComplet"
          :role="libelleRole"
          @fermer="menuCompteOuvert = false"
          @deconnexion="deconnecter"
        />
      </div>

      <main
        id="hz-contenu"
        class="hz-app__contenu"
        tabindex="-1"
      >
        <BandeauReseau
          :erreur="incidentReseau"
          :en-cours="repriseEnCours"
          @reessayer="reessayerReseau"
        />
        <RouterView />
      </main>
    </div>

    <ZoneToasts />
  </div>
</template>

<style scoped>
.hz-app__burger svg {
  width: 18px;
  height: 14px;
}
</style>
